/* eslint-disable @next/next/no-img-element */
import { type SerializedUploadNode } from '@payloadcms/richtext-lexical';
import { type JSXConverters } from '@payloadcms/richtext-lexical/react';
import { cn } from '@tallinn-arvudes/ui/utils';
import {
  type FileData,
  type FileSizeImproved,
  type JsonObject,
  type TypedUploadCollection,
  type TypeWithID,
  type UploadCollectionSlug,
} from 'payload';
import { type ReactNode } from 'react';

export type UploadDataImproved<
  TUploadExtraFieldsData extends JsonObject = JsonObject,
> = {
  [TCollectionSlug in UploadCollectionSlug]: {
    fields: TUploadExtraFieldsData;
    /**
     * Every lexical node that has sub-fields needs to have a unique ID. This is the ID of this upload node, not the ID of the linked upload document
     */
    id: string;
    relationTo: TCollectionSlug;
    /**
     * Value can be just the document ID, or the full, populated document
     */
    value: number | string | TypedUploadCollection[TCollectionSlug];
  };
}[UploadCollectionSlug];

export const UploadJSXConverter: JSXConverters<SerializedUploadNode> = {
  upload: ({ node }) => {
    // TO-DO (v4): SerializedUploadNode should use UploadData_P4
    const uploadNode = node as UploadDataImproved;
    if (typeof uploadNode.value !== 'object') {
      return null;
    }

    const uploadDoc = uploadNode.value as unknown as FileData & TypeWithID;

    const url = uploadDoc.url;
    /**
     * If the upload is not an image, return a link to the upload
     */
    if (!uploadDoc.mimeType.startsWith('image')) {
      return (
        <a href={url} rel="noopener noreferrer">
          {uploadDoc.filename}
        </a>
      );
    }

    const className = cn({
      'justify-self-center my-4':
        !node.fields || node.fields?.alignment === 'center',
      'justify-self-start my-4': node.fields?.alignment === 'left',
      'justify-self-end my-4': node.fields?.alignment === 'right',
      'float-left mr-4': node.fields?.alignment === 'left' && node.fields.float,
      'float-right ml-4':
        node.fields?.alignment === 'right' && node.fields.float,
    });

    /**
     * If the upload is a simple image with no different sizes, return a simple img tag
     */
    if (!uploadDoc.sizes || !Object.keys(uploadDoc.sizes).length) {
      return (
        <img
          className={className}
          alt={uploadDoc.filename}
          height={uploadDoc.height}
          src={url}
          width={uploadDoc.width}
        />
      );
    }

    /**
     * If the upload is an image with different sizes, return a picture element
     */
    const pictureJSX: ReactNode[] = [];

    // Iterate through each size in the data.sizes object
    for (const size in uploadDoc.sizes) {
      const imageSize = uploadDoc.sizes[size] as FileSizeImproved;

      // Skip if any property of the size object is null
      if (
        !imageSize ||
        !imageSize.width ||
        !imageSize.height ||
        !imageSize.mimeType ||
        !imageSize.filesize ||
        !imageSize.filename ||
        !imageSize.url
      ) {
        continue;
      }
      const imageSizeURL = imageSize?.url;

      pictureJSX.push(
        <source
          className={className}
          key={size}
          media={`(max-width: ${imageSize.width}px)`}
          srcSet={imageSizeURL!}
          type={imageSize.mimeType!}
        />,
      );
    }

    // Add the default img tag
    pictureJSX.push(
      <img
        className={className}
        alt={uploadDoc?.filename}
        height={uploadDoc?.height}
        key={'image'}
        src={url}
        width={uploadDoc?.width}
      />,
    );
    return <picture>{pictureJSX}</picture>;
  },
};
